/**
 * frontend/src/pages/valuation/_signOffReissue.ts
 *
 * VAL-REISSUE-SERVER-1 — plain labels for the sign-off "Changed since vN: ..." line.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 */

/**
 * Plain labels for the "Changed since vN: ..." line. Whether anything changed
 * is decided once, by the server (`preflight.reissue`, VAL-REISSUE-SERVER-1) —
 * this screen only names the fields it is told moved. `answers` is the key
 * `inputs_to_dict()` uses for `RecomputeBody.driver_answers`.
 */
export const REISSUE_FIELD_LABELS: Record<string, string> = {
  benchmark_key: 'benchmark',
  normalized_earnings: 'normalized earnings',
  basis: 'earnings basis',
  income_value: 'income approach value',
  total_assets: 'total assets',
  total_liabilities: 'total liabilities',
  fair_value_adjustments: 'fair value adjustments',
  override_multiple: 'override multiple',
  override_rationale: 'override rationale',
  override_disposition: 'override disposition',
  naics_code: 'NAICS code',
  sub_vertical: 'sub-vertical',
  market_only_acknowledged: 'market-only acknowledgement',
  risk_free_rate: 'risk-free rate',
  company_specific_risk: 'company-specific risk',
  long_term_growth_rate: 'long-term growth rate',
  equity_risk_premium: 'equity risk premium',
  size_premium: 'size premium',
  growth_rate: 'growth rate',
  projection_years: 'projection years',
  capitalized_earnings_weight: 'capitalized earnings weight',
  base_cash_flow: 'base cash flow',
  replacement_compensation: 'replacement compensation',
  comp_multiples: 'comparable company multiples',
  // WF-C-4 FINAL (WALKFIX-C AMEND_1).
  config_fingerprint: 'method settings',
}

/** "3 driver answers" for `answers` (counting individual differing entries,
 *  not the one field name) — every other field maps to its plain label. */
export function humanizeChangedField(
  key: string,
  frozenInputs: Record<string, unknown>,
  candidateAnswers: Record<string, string>,
): string {
  if (key === 'answers') {
    const frozen = (frozenInputs.answers as Record<string, string> | undefined) ?? {}
    const keys = new Set([...Object.keys(frozen), ...Object.keys(candidateAnswers)])
    let count = 0
    keys.forEach(k => { if (frozen[k] !== candidateAnswers[k]) count += 1 })
    return `${count} driver answer${count === 1 ? '' : 's'}`
  }
  return REISSUE_FIELD_LABELS[key] ?? key
}
